// Bausteine des Freigabe-Workflows: Einreichen-Dialog (anstelle von Veröffentlichen), Status „Wartet auf Freigabe“
// und Hinweis im Editor.
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { submitPath, useApprovalState, usePendingFor, useRefreshApprovals, type ApprovalRequest, type ApprovalTargetType } from '../api/approvals';
import { useMe } from '../api/queries';
import { localeOf } from '../i18n';
import { Icon } from './Icon';
import { useToast } from './toast';
import { Dialog, Field, formatDateTime } from './ui';

/** Freigabe eingeschaltet? Dann wird eingereicht statt veröffentlicht. */
export function useApprovalRequired(): boolean {
  const { data } = useApprovalState();
  return !!data?.enabled;
}

export function SubmitDialog({ type, id, name, body, onClose, onSubmitted, children }: {
  type: ApprovalTargetType; id: string; name: string;
  /** Zusätzlicher Rumpf (Zeitplan: vollständiger Stand wie bei PUT /schedules/{id}). */
  body?: Record<string, unknown>;
  onClose: () => void; onSubmitted?: (r: ApprovalRequest) => void; children?: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { data: me } = useMe();
  const { data: state } = useApprovalState();
  const pending = usePendingFor(type, id);
  const refresh = useRefreshApprovals();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const own = pending && pending.submittedBy.id === me?.user.id ? pending : undefined;
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post<ApprovalRequest>(submitPath(type, id), { ...body, ...(note.trim() ? { note: note.trim() } : {}) });
      await refresh();
      toast.show(t('approval.submit.done'));
      onSubmitted?.(r);
      onClose();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog title={t('approval.submit.title', { name })} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
      <button className="btn primary" disabled={busy} onClick={() => void submit()}><Icon name="send" />{t('approval.submit.send')}</button>
    </>}>
      <div className="hint">{t('approval.submit.text')}</div>
      {children}
      <Field label={t('approval.submit.note')}>
        <textarea value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} placeholder={t('approval.submit.notePh')} />
      </Field>
      {own && <div className="hint warn">{t('approval.submit.replaces', { when: formatDateTime(own.submittedAt, localeOf(i18n.language)) })}</div>}
      {state?.canSelfApprove && <div className="muted small">{t('approval.submit.selfHint')}</div>}
    </Dialog>
  );
}

export function PendingPill({ request }: { request: ApprovalRequest | undefined }) {
  const { t } = useTranslation();
  if (!request) return null;
  return <span className="pill warn" title={request.note ?? undefined}>{t('approval.pending.pill')}</span>;
}

/** Hinweis im Editor: es wartet eine Anfrage auf Freigabe. */
export function PendingBanner({ type, id, children }: { type: ApprovalTargetType; id: string; children?: ReactNode }) {
  const { t, i18n } = useTranslation();
  const r = usePendingFor(type, id);
  if (!r) return null;
  return (
    <div className="hint warn row" role="status" style={{ marginBottom: 12, flexWrap: 'wrap' }} data-testid="approval-pending">
      <span className="grow">
        {t('approval.pending.banner', { who: r.submittedBy.label, when: formatDateTime(r.submittedAt, localeOf(i18n.language)) })}
        {children ? <> {children}</> : null}
      </span>
      <Link className="btn sm" to={`/approvals/${r.id}`}>{t('approval.pending.show')}</Link>
    </div>
  );
}
